import { useState, useCallback, useRef, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import { toast } from "sonner";
import { 
  Upload, 
  FileText, 
  FileSpreadsheet, 
  Image as ImageIcon,
  File,
  X,
  CheckCircle,
  Loader2,
  Eye,
  Trash2,
  RefreshCw,
  Camera,
  Sparkles,
  Brain,
  Receipt,
  ShoppingCart,
  TrendingDown,
  TrendingUp,
  Euro,
  Calendar,
  Building2,
  Plus,
  Save,
  Edit2,
  Download,
  AlertTriangle,
  FolderOpen,
  Filter
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import ManuelleEingabeTab from "../components/dokumente/ManuelleEingabeTab";
import AusgabenHistoryTab from "../components/dokumente/AusgabenHistoryTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Euro-Cent Parser: "12.90" oder "12,90" → 12.90
const parseEuroCent = (value) => {
  if (!value) return 0;
  const str = String(value).trim();
  const normalized = str.replace(',', '.');
  const num = parseFloat(normalized);
  return isNaN(num) ? 0 : Math.round(num * 100) / 100;
};

// Euro Formatter
const formatEuro = (value) => {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value || 0);
};

// File Size Formatter
const formatSize = (bytes) => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// File Icons
const getFileIcon = (filename) => {
  const ext = filename?.split('.').pop()?.toLowerCase();
  if (['xlsx', 'xls', 'csv'].includes(ext)) return <FileSpreadsheet className="text-green-500" size={24} />;
  if (['pdf'].includes(ext)) return <FileText className="text-red-500" size={24} />;
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic'].includes(ext)) return <ImageIcon className="text-blue-500" size={24} />;
  return <File className="text-slate-400" size={24} />;
};

const DateiImport = () => {
  const [activeTab, setActiveTab] = useState("upload");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  
  // Dokumenttyp: Einkauf oder Verkauf - WICHTIG!
  const [dokumentTyp, setDokumentTyp] = useState("einkauf");
  const [kategorie, setKategorie] = useState("Rechnung");
  
  const cameraInputRef = useRef(null);

  // Gespeicherte Dokumente
  const [savedDokumente, setSavedDokumente] = useState([]);
  const [loadingDokumente, setLoadingDokumente] = useState(false);
  const [dokumenteFilter, setDokumenteFilter] = useState("alle");
  
  // Delete Dialog State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dokumentToDelete, setDokumentToDelete] = useState(null);
  const [deleteKeepFile, setDeleteKeepFile] = useState(false);

  // Manuelle Eingabe und Letzte Ausgaben sind jetzt in eigenen Komponenten

  // Statistiken
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchSavedDokumente();
    fetchStats();
  }, []);

  const fetchSavedDokumente = async () => {
    setLoadingDokumente(true);
    try {
      const params = dokumenteFilter !== "alle" ? `?dokumenttyp=${dokumenteFilter}` : "";
      const response = await axios.get(`${API}/dokumente${params}`, { withCredentials: true });
      setSavedDokumente(response.data || []);
    } catch (error) {
      console.error("Fehler beim Laden der Dokumente:", error);
    } finally {
      setLoadingDokumente(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dokumente/statistik`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error("Statistik-Fehler:", error);
    }
  };

  useEffect(() => {
    fetchSavedDokumente();
  }, [dokumenteFilter]);

  // Dropzone
  const onDrop = useCallback((acceptedFiles) => {
    const newFiles = acceptedFiles.map(file => ({
      file,
      id: `${file.name}-${Date.now()}-${Math.random()}`,
      status: 'pending',
      progress: 0,
      result: null,
      error: null,
      savedId: null
    }));
    setFiles(prev => [...prev, ...newFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, multiple: true });

  const removeFile = (fileId) => setFiles(prev => prev.filter(f => f.id !== fileId));
  const clearAll = () => setFiles([]);

  // Kamera
  const handleCameraCapture = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      const newFile = {
        file,
        id: `camera-${Date.now()}`,
        status: 'pending',
        progress: 0,
        result: null,
        error: null,
        savedId: null
      };
      setFiles(prev => [...prev, newFile]);
      toast.success('Foto aufgenommen!');
    }
  };

  // Upload mit Speicherung
  const uploadFile = async (fileObj) => {
    const formData = new FormData();
    formData.append('file', fileObj.file);
    formData.append('dokumenttyp', dokumentTyp);
    formData.append('kategorie', kategorie);

    try {
      setFiles(prev => prev.map(f => 
        f.id === fileObj.id ? { ...f, status: 'uploading', progress: 0 } : f
      ));

      // 1. Zuerst Dokument speichern
      const saveResponse = await axios.post(`${API}/dokumente/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 50) / progressEvent.total);
          setFiles(prev => prev.map(f => 
            f.id === fileObj.id ? { ...f, progress } : f
          ));
        }
      });

      const savedId = saveResponse.data.id;

      // 2. Dann KI-Analyse starten
      setFiles(prev => prev.map(f => 
        f.id === fileObj.id ? { ...f, progress: 60, savedId } : f
      ));

      const analyzeResponse = await axios.post(`${API}/dokumente/${savedId}/ki-analyse`, {}, {
        withCredentials: true
      });

      setFiles(prev => prev.map(f => 
        f.id === fileObj.id ? { 
          ...f, 
          status: 'success', 
          progress: 100, 
          result: analyzeResponse.data,
          savedId 
        } : f
      ));

      return { ...analyzeResponse.data, savedId };
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Upload fehlgeschlagen';
      setFiles(prev => prev.map(f => 
        f.id === fileObj.id ? { ...f, status: 'error', error: errorMsg } : f
      ));
      throw error;
    }
  };

  const uploadAll = async () => {
    const pendingFiles = files.filter(f => f.status === 'pending');
    if (pendingFiles.length === 0) {
      toast.info('Keine Dateien zum Hochladen');
      return;
    }

    setUploading(true);
    let successCount = 0;
    let errorCount = 0;

    for (const fileObj of pendingFiles) {
      try {
        await uploadFile(fileObj);
        successCount++;
      } catch {
        errorCount++;
      }
    }

    setUploading(false);
    fetchSavedDokumente();
    fetchStats();
    
    if (successCount > 0) {
      toast.success(`${successCount} Dokument(e) als ${dokumentTyp === 'einkauf' ? 'Einkauf' : 'Verkauf'} gespeichert & analysiert!`);
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} Datei(en) fehlgeschlagen`);
    }
  };

  // Import bestätigen
  const importData = async (fileObj) => {
    const result = fileObj.result;
    const savedId = fileObj.savedId;
    
    if (!savedId) {
      toast.error('Dokument nicht gespeichert');
      return;
    }

    try {
      const response = await axios.post(`${API}/dokumente/${savedId}/importieren`, {
        eintraege: result?.alle_eintraege || result?.vorschau || []
      }, { withCredentials: true });

      if (response.data.erfolg) {
        toast.success(`${response.data.importiert} Positionen als ${dokumentTyp === 'einkauf' ? 'Ausgaben' : 'Einnahmen'} importiert`);
        setFiles(prev => prev.map(f => 
          f.id === fileObj.id ? { ...f, imported: true } : f
        ));
        fetchRecentExpenses();
        fetchSavedDokumente();
      }
    } catch (error) {
      toast.error('Import fehlgeschlagen: ' + (error.response?.data?.detail || error.message));
    }
  };

  const showPreview = (fileObj) => {
    setPreviewData(fileObj.result);
    setPreviewOpen(true);
  };

  // Dokument löschen - Dialog öffnen
  const openDeleteDialog = (dokument) => {
    setDokumentToDelete(dokument);
    setDeleteKeepFile(false);
    setDeleteDialogOpen(true);
  };

  // Dokument tatsächlich löschen
  const confirmDelete = async () => {
    if (!dokumentToDelete) return;

    try {
      await axios.delete(`${API}/dokumente/${dokumentToDelete.id}?behalte_datei=${deleteKeepFile}`, {
        withCredentials: true
      });
      
      toast.success(
        deleteKeepFile 
          ? 'Dokument gelöscht, Transaktionen behalten' 
          : 'Dokument und verknüpfte Daten gelöscht'
      );
      
      fetchSavedDokumente();
      fetchStats();
      fetchRecentExpenses();
    } catch (error) {
      toast.error('Löschen fehlgeschlagen: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDeleteDialogOpen(false);
      setDokumentToDelete(null);
    }
  };

  // Download Dokument
  const downloadDokument = async (dokument) => {
    try {
      const response = await axios.get(`${API}/dokumente/${dokument.id}/download`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', dokument.dateiname);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Download fehlgeschlagen');
    }
  };

  // KI-Analyse für gespeichertes Dokument
  const analyzeExistingDokument = async (dokument) => {
    try {
      toast.info('KI-Analyse läuft...');
      const response = await axios.post(`${API}/dokumente/${dokument.id}/ki-analyse`, {}, {
        withCredentials: true
      });
      toast.success(`${response.data.erkannte_eintraege} Positionen erkannt!`);
      fetchSavedDokumente();
      
      // Preview anzeigen
      setPreviewData(response.data);
      setPreviewOpen(true);
    } catch (error) {
      toast.error('KI-Analyse fehlgeschlagen: ' + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white mb-2 flex items-center gap-3">
          <Receipt className="text-amber-400" />
          Dokumenten-Management
        </h1>
        <p className="text-slate-400">
          Rechnungen & Belege hochladen, speichern und verwalten
        </p>
      </div>

      {/* Statistik-Karten */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
            <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
              <FolderOpen size={16} />
              Gesamt
            </div>
            <p className="text-2xl font-bold text-white">{stats.gesamt?.anzahl || 0}</p>
            <p className="text-xs text-slate-500">{formatSize(stats.gesamt?.gesamtgroesse || 0)}</p>
          </div>
          <div className="p-4 bg-red-900/20 rounded-lg border border-red-700/50">
            <div className="flex items-center gap-2 text-red-400 text-sm mb-1">
              <TrendingDown size={16} />
              Einkauf
            </div>
            <p className="text-2xl font-bold text-white">{stats.einkauf?.anzahl || 0}</p>
            <p className="text-xs text-slate-500">{stats.einkauf?.importiert || 0} importiert</p>
          </div>
          <div className="p-4 bg-green-900/20 rounded-lg border border-green-700/50">
            <div className="flex items-center gap-2 text-green-400 text-sm mb-1">
              <TrendingUp size={16} />
              Verkauf
            </div>
            <p className="text-2xl font-bold text-white">{stats.verkauf?.anzahl || 0}</p>
            <p className="text-xs text-slate-500">{stats.verkauf?.importiert || 0} importiert</p>
          </div>
          <div className="p-4 bg-violet-900/20 rounded-lg border border-violet-700/50">
            <div className="flex items-center gap-2 text-violet-400 text-sm mb-1">
              <Brain size={16} />
              KI-Analysiert
            </div>
            <p className="text-2xl font-bold text-white">{stats.gesamt?.analysiert || 0}</p>
            <p className="text-xs text-slate-500">von {stats.gesamt?.anzahl || 0}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-slate-800/50 border border-slate-700">
          <TabsTrigger value="upload" className="data-[state=active]:bg-violet-600" data-testid="tab-upload">
            <Upload size={16} className="mr-2" /> Hochladen
          </TabsTrigger>
          <TabsTrigger value="dokumente" className="data-[state=active]:bg-violet-600" data-testid="tab-dokumente">
            <FolderOpen size={16} className="mr-2" /> Gespeicherte Dokumente
          </TabsTrigger>
          <TabsTrigger value="manual" className="data-[state=active]:bg-violet-600" data-testid="tab-manual">
            <Edit2 size={16} className="mr-2" /> Manuelle Eingabe
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-violet-600" data-testid="tab-history">
            <Receipt size={16} className="mr-2" /> Letzte Ausgaben
          </TabsTrigger>
        </TabsList>

        {/* Upload Tab */}
        <TabsContent value="upload" className="space-y-6">
          {/* WICHTIG: Einkauf/Verkauf Auswahl */}
          <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700">
            <h3 className="text-white font-medium mb-4 flex items-center gap-2">
              <AlertTriangle className="text-amber-400" size={18} />
              Dokumenttyp festlegen
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => setDokumentTyp("einkauf")}
                className={`p-4 rounded-lg border-2 transition-all ${
                  dokumentTyp === "einkauf" 
                    ? "border-red-500 bg-red-500/20" 
                    : "border-slate-600 hover:border-slate-500"
                }`}
                data-testid="btn-einkauf"
              >
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-lg ${dokumentTyp === "einkauf" ? "bg-red-500" : "bg-slate-700"}`}>
                    <ShoppingCart className="text-white" size={24} />
                  </div>
                  <div className="text-left">
                    <p className="text-white font-semibold">Einkauf / Ausgabe</p>
                    <p className="text-sm text-slate-400">Lieferanten-Rechnungen, Wareneinkauf</p>
                  </div>
                </div>
              </button>
              
              <button
                onClick={() => setDokumentTyp("verkauf")}
                className={`p-4 rounded-lg border-2 transition-all ${
                  dokumentTyp === "verkauf" 
                    ? "border-green-500 bg-green-500/20" 
                    : "border-slate-600 hover:border-slate-500"
                }`}
                data-testid="btn-verkauf"
              >
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-lg ${dokumentTyp === "verkauf" ? "bg-green-500" : "bg-slate-700"}`}>
                    <Euro className="text-white" size={24} />
                  </div>
                  <div className="text-left">
                    <p className="text-white font-semibold">Verkauf / Einnahme</p>
                    <p className="text-sm text-slate-400">Kassenabrechnungen, Einnahmen-Belege</p>
                  </div>
                </div>
              </button>
            </div>

            <div className="mt-4">
              <label className="text-sm text-slate-400 block mb-2">Kategorie</label>
              <Select value={kategorie} onValueChange={setKategorie}>
                <SelectTrigger className="w-full md:w-[300px] bg-slate-700 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {dokumentTyp === "einkauf" ? (
                    <>
                      <SelectItem value="Rechnung">Rechnung</SelectItem>
                      <SelectItem value="Lieferschein">Lieferschein</SelectItem>
                      <SelectItem value="Wareneinkauf">Wareneinkauf</SelectItem>
                      <SelectItem value="Getränke">Getränke</SelectItem>
                      <SelectItem value="Lebensmittel">Lebensmittel</SelectItem>
                      <SelectItem value="Verbrauchsmaterial">Verbrauchsmaterial</SelectItem>
                      <SelectItem value="Fixkosten">Fixkosten</SelectItem>
                    </>
                  ) : (
                    <>
                      <SelectItem value="Kassenbericht">Kassenbericht</SelectItem>
                      <SelectItem value="Tagesabrechnung">Tagesabrechnung</SelectItem>
                      <SelectItem value="Verkaufsbeleg">Verkaufsbeleg</SelectItem>
                      <SelectItem value="EC-Abrechnung">EC-Abrechnung</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Upload Zone */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div
              {...getRootProps()}
              className={`col-span-2 border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                isDragActive 
                  ? dokumentTyp === "einkauf" 
                    ? 'border-red-500 bg-red-500/10' 
                    : 'border-green-500 bg-green-500/10'
                  : 'border-slate-600 hover:border-slate-500 bg-slate-800/50'
              }`}
              data-testid="dropzone"
            >
              <input {...getInputProps()} />
              <Upload className={`mx-auto mb-4 ${
                isDragActive 
                  ? dokumentTyp === "einkauf" ? 'text-red-400' : 'text-green-400'
                  : 'text-slate-400'
              }`} size={40} />
              <p className="text-lg text-slate-300 mb-2">
                {isDragActive 
                  ? `${dokumentTyp === "einkauf" ? "Einkaufs-" : "Verkaufs-"}Beleg ablegen...`
                  : 'Dokumente hier ablegen'}
              </p>
              <p className="text-sm text-slate-500">
                PDF, Foto, Excel - Dateien werden gespeichert & KI analysiert
              </p>
              <div className={`mt-4 inline-block px-3 py-1 rounded-full text-sm ${
                dokumentTyp === "einkauf" 
                  ? "bg-red-500/20 text-red-400"
                  : "bg-green-500/20 text-green-400"
              }`}>
                {dokumentTyp === "einkauf" ? "Einkauf" : "Verkauf"}
              </div>
            </div>

            <div 
              className="border-2 border-dashed border-slate-600 rounded-xl p-8 text-center cursor-pointer transition-all hover:border-blue-500 hover:bg-blue-500/10 flex flex-col items-center justify-center"
              onClick={() => cameraInputRef.current?.click()}
              data-testid="camera-btn"
            >
              <Camera className="text-blue-400 mb-3" size={40} />
              <p className="text-slate-300 font-medium">Beleg fotografieren</p>
              <p className="text-sm text-slate-500 mt-1">Kamera öffnen</p>
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleCameraCapture}
                className="hidden"
              />
            </div>
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white">Dateien ({files.length})</h2>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={clearAll} className="border-slate-600">
                    <Trash2 size={14} className="mr-1" /> Alle entfernen
                  </Button>
                  <Button 
                    onClick={uploadAll} 
                    disabled={uploading || files.every(f => f.status !== 'pending')} 
                    className={dokumentTyp === "einkauf" ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"}
                    data-testid="upload-all-btn"
                  >
                    {uploading 
                      ? <><Loader2 size={14} className="mr-1 animate-spin" /> Analysiere...</>
                      : <><Brain size={14} className="mr-1" /> Hochladen & Analysieren</>
                    }
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                {files.map((fileObj) => (
                  <div 
                    key={fileObj.id}
                    className={`p-4 rounded-lg border transition-all ${
                      fileObj.status === 'success' ? 'bg-green-900/20 border-green-700/50' :
                      fileObj.status === 'error' ? 'bg-red-900/20 border-red-700/50' :
                      fileObj.status === 'uploading' ? 'bg-amber-900/20 border-amber-700/50' :
                      'bg-slate-800/50 border-slate-700'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      {getFileIcon(fileObj.file.name)}
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{fileObj.file.name}</p>
                        <p className="text-sm text-slate-400">{formatSize(fileObj.file.size)}</p>
                        
                        {fileObj.status === 'uploading' && (
                          <div className="mt-2 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                            <div className="h-full bg-amber-500 transition-all" style={{ width: `${fileObj.progress}%` }} />
                          </div>
                        )}

                        {fileObj.status === 'error' && <p className="text-sm text-red-400 mt-1">{fileObj.error}</p>}

                        {fileObj.status === 'success' && fileObj.result && (
                          <div className="mt-2 text-sm">
                            <span className="text-green-400">
                              <CheckCircle className="inline mr-1" size={14} />
                              {fileObj.result.erkannte_eintraege || 0} Positionen erkannt
                            </span>
                            {fileObj.result.lieferant && <span className="text-slate-400 ml-2">| {fileObj.result.lieferant}</span>}
                            {fileObj.result.gesamtbetrag > 0 && (
                              <span className="text-amber-400 ml-2 font-mono">| {formatEuro(fileObj.result.gesamtbetrag)}</span>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        {fileObj.status === 'success' && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => showPreview(fileObj)} className="text-slate-300">
                              <Eye size={14} />
                            </Button>
                            {!fileObj.imported && (
                              <Button size="sm" onClick={() => importData(fileObj)} className="bg-green-600 hover:bg-green-700">
                                <Save size={14} className="mr-1" /> Importieren
                              </Button>
                            )}
                            {fileObj.imported && <CheckCircle className="text-green-400" size={20} />}
                          </>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => removeFile(fileObj.id)} className="text-slate-400 hover:text-red-400">
                          <X size={14} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Info Box */}
          <div className={`p-4 rounded-lg border ${
            dokumentTyp === "einkauf" 
              ? "bg-red-900/20 border-red-700/50"
              : "bg-green-900/20 border-green-700/50"
          }`}>
            <div className="flex items-start gap-3">
              <Sparkles className={dokumentTyp === "einkauf" ? "text-red-400" : "text-green-400"} size={20} />
              <div>
                <h3 className={`font-medium mb-1 ${dokumentTyp === "einkauf" ? "text-red-300" : "text-green-300"}`}>
                  {dokumentTyp === "einkauf" ? "Einkaufs-Dokumente" : "Verkaufs-Dokumente"}
                </h3>
                <p className="text-slate-400 text-sm">
                  {dokumentTyp === "einkauf" 
                    ? "Lieferanten-Rechnungen, Wareneinkauf, Betriebskosten - werden als Ausgaben erfasst."
                    : "Kassenabrechnungen, Verkaufsbelege - werden als Einnahmen erfasst."
                  }
                </p>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Gespeicherte Dokumente Tab */}
        <TabsContent value="dokumente" className="space-y-6">
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <FolderOpen className="text-amber-400" />
                Gespeicherte Dokumente
              </h2>
              <div className="flex items-center gap-2">
                <Filter size={16} className="text-slate-400" />
                <Select value={dokumenteFilter} onValueChange={setDokumenteFilter}>
                  <SelectTrigger className="w-[150px] bg-slate-700 border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="alle">Alle</SelectItem>
                    <SelectItem value="einkauf">Einkauf</SelectItem>
                    <SelectItem value="verkauf">Verkauf</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" onClick={fetchSavedDokumente} className="border-slate-600">
                  <RefreshCw size={14} className={loadingDokumente ? 'animate-spin' : ''} />
                </Button>
              </div>
            </div>

            {loadingDokumente ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="animate-spin text-slate-400" size={24} />
              </div>
            ) : savedDokumente.length > 0 ? (
              <div className="space-y-3">
                {savedDokumente.map((dok) => (
                  <div key={dok.id} className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      {getFileIcon(dok.dateiname)}
                      <div>
                        <p className="text-white font-medium">{dok.dateiname}</p>
                        <div className="flex items-center gap-2 text-sm">
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            dok.dokumenttyp === "einkauf" 
                              ? "bg-red-500/20 text-red-400" 
                              : "bg-green-500/20 text-green-400"
                          }`}>
                            {dok.dokumenttyp === "einkauf" ? "Einkauf" : "Verkauf"}
                          </span>
                          <span className="text-slate-500">|</span>
                          <span className="text-slate-400">{dok.kategorie}</span>
                          <span className="text-slate-500">|</span>
                          <span className="text-slate-500">{formatSize(dok.dateigroesse)}</span>
                          {dok.ki_analysiert && (
                            <>
                              <span className="text-slate-500">|</span>
                              <span className="text-violet-400"><Brain size={12} className="inline" /> Analysiert</span>
                            </>
                          )}
                          {dok.importiert && (
                            <>
                              <span className="text-slate-500">|</span>
                              <span className="text-green-400"><CheckCircle size={12} className="inline" /> Importiert</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!dok.ki_analysiert && (
                        <Button size="sm" variant="outline" onClick={() => analyzeExistingDokument(dok)} className="border-violet-600 text-violet-400">
                          <Brain size={14} className="mr-1" /> Analysieren
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => downloadDokument(dok)} className="text-slate-400">
                        <Download size={14} />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => openDeleteDialog(dok)} 
                        className="text-slate-400 hover:text-red-400"
                        data-testid={`delete-dok-${dok.id}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <FolderOpen size={40} className="mx-auto mb-3 opacity-30" />
                <p>Noch keine Dokumente gespeichert</p>
                <Button onClick={() => setActiveTab("upload")} className="mt-4 bg-violet-600">
                  Erstes Dokument hochladen
                </Button>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Manuelle Eingabe Tab */}
        <TabsContent value="manual" className="space-y-6">
          <ManuelleEingabeTab onExpenseAdded={() => { fetchRecentExpenses(); fetchStats(); }} />
        </TabsContent>

        {/* Letzte Ausgaben Tab */}
        <TabsContent value="history" className="space-y-6">
          <AusgabenHistoryTab onSwitchToManual={() => setActiveTab("manual")} />
        </TabsContent>
      </Tabs>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-auto bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Brain className="text-amber-400" size={20} />
              KI-Analyse Ergebnis
            </DialogTitle>
            <DialogDescription className="text-slate-400">Erkannte Positionen aus dem Dokument</DialogDescription>
          </DialogHeader>
          
          {previewData && (
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-900 rounded-lg">
                <div>
                  <span className="text-amber-400 text-sm">Dokumenttyp:</span>
                  <p className="text-white">{previewData.typ || 'Rechnung'}</p>
                </div>
                <div>
                  <span className="text-amber-400 text-sm">Lieferant:</span>
                  <p className="text-white">{previewData.lieferant || '-'}</p>
                  {/* Automatische Lieferanten-Erkennung */}
                  {previewData.lieferant_erkannt && (
                    <div className="mt-1 flex items-center gap-2">
                      <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded">
                        ✓ Erkannt: {previewData.lieferant_erkannt.name}
                      </span>
                      <span className="text-xs text-slate-500">{previewData.lieferant_erkannt.typ}</span>
                    </div>
                  )}
                </div>
                <div>
                  <span className="text-amber-400 text-sm">Datum:</span>
                  <p className="text-white">{previewData.datum || '-'}</p>
                </div>
                <div>
                  <span className="text-amber-400 text-sm">Gesamtbetrag:</span>
                  <p className="text-white font-mono">{formatEuro(previewData.gesamtbetrag)}</p>
                </div>
              </div>

              {(previewData.vorschau || previewData.alle_eintraege)?.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-700">
                        <th className="text-left p-2 text-slate-300">Artikel</th>
                        <th className="text-right p-2 text-slate-300">Menge</th>
                        <th className="text-left p-2 text-slate-300">Einheit</th>
                        <th className="text-right p-2 text-slate-300">Einzelpreis</th>
                        <th className="text-right p-2 text-slate-300">Gesamt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(previewData.vorschau || previewData.alle_eintraege).slice(0, 20).map((item, i) => (
                        <tr key={i} className="border-b border-slate-700/50">
                          <td className="p-2 text-white">{item.artikel || item.name || '-'}</td>
                          <td className="p-2 text-slate-400 text-right">{item.menge || 1}</td>
                          <td className="p-2 text-slate-400">{item.einheit || '-'}</td>
                          <td className="p-2 text-slate-400 text-right">{formatEuro(item.einzelpreis)}</td>
                          <td className="p-2 text-white text-right font-mono">{formatEuro(item.gesamtpreis)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-slate-800 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="text-amber-400" />
              Dokument löschen?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {dokumentToDelete && (
                <>
                  <p className="mb-4">
                    Sind Sie sicher, dass Sie <strong className="text-white">"{dokumentToDelete.dateiname}"</strong> löschen möchten?
                  </p>
                  <div className="space-y-3 bg-slate-700/50 p-4 rounded-lg">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="deleteOption"
                        checked={!deleteKeepFile}
                        onChange={() => setDeleteKeepFile(false)}
                        className="w-4 h-4 text-red-600"
                      />
                      <div>
                        <p className="text-white font-medium">Alles löschen</p>
                        <p className="text-sm text-slate-400">Dokument und alle verknüpften Ausgaben/Einnahmen werden gelöscht</p>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="deleteOption"
                        checked={deleteKeepFile}
                        onChange={() => setDeleteKeepFile(true)}
                        className="w-4 h-4 text-amber-600"
                      />
                      <div>
                        <p className="text-white font-medium">Nur Dokument löschen, Daten behalten</p>
                        <p className="text-sm text-slate-400">Die bereits importierten Ausgaben/Einnahmen bleiben erhalten</p>
                      </div>
                    </label>
                  </div>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 border-slate-600 text-white hover:bg-slate-600">
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className={deleteKeepFile ? "bg-amber-600 hover:bg-amber-700" : "bg-red-600 hover:bg-red-700"}
            >
              {deleteKeepFile ? "Dokument löschen, Daten behalten" : "Alles löschen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DateiImport;
